import * as React from "react";
import { useFormContext } from "react-hook-form";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import { DomainInput } from "./types/input";
import { FormDryRunAlert } from "./formDryRunAlert";
import { isApex } from "../../../services/utils";
import NGAlert from "../../../newcomponents/alert";
import { FormNGRadioGroup } from "./components/FormNGRadioGroup";
import { FormRadio } from "./components/FormRadio";
import { FormNGCheckboxGroup } from "./components/FormNGCheckboxGroup";
import { CheckCircleFilled } from "@ant-design/icons";

interface Props {}
export const CreateNGAdvancedGeneral: React.FC<Props> = ({}) => {
  const { control, watch, setValue, getValues } = useFormContext<DomainInput>();

  const dryRunNameOnly = watch("dryRun.nameOnly.value") || {};
  const name = watch("advanced.name");
  const isDomainApex = React.useMemo(() => {
    return isApex(name);
  }, [name]);

  const dnsMode = watch("advanced.spec.dnsMode");
  const routingMode = watch("advanced.spec.routingMode");

  React.useEffect(() => {
    if (routingMode === "workloadSubdomain" || dnsMode === "ns") {
      setValue("advanced.spec.certChallengeType", "dns01");
    }
  }, [dnsMode, routingMode, setValue]);

  React.useEffect(() => {
    if (routingMode === "gvcSubdomain") {
      const currentValues = getValues();
      const ports = currentValues.advanced?.spec?.ports || [];

      const clearedPorts = ports.map((port) => ({ ...port, routes: [] }));

      setValue("advanced.spec.ports", clearedPorts);
    }
  }, [routingMode, setValue, getValues]);

  if (dryRunNameOnly && dryRunNameOnly.status !== 200) {
    return null;
  }

  return (
    <div className="color-default">
      <div className="mb-8">
        <div className="flex items-center">
          <NGLabelText className="cursor-default">DNS Mode</NGLabelText>
          <span className="color-danger">*</span>
        </div>
        <div className="mt-2 flex gap-4">
          {/* TODO radio-card can be a component for form directly */}
          <div
            className={`mb-2 p-4 border rounded cursor-pointer radio-card ${
              dnsMode === "cname" ? "radio-card-active" : ""
            }`}
            onClick={() => setValue("advanced.spec.dnsMode", "cname")}
          >
            <FormRadio
              name="advanced.spec.dnsMode"
              control={control}
              compareValue="cname"
              rules={{ required: "DNS mode is required" }}
            >
              <div className="flex items-center gap-2">
                <div>CNAME</div>
                {dnsMode === "cname" ? <CheckCircleFilled className="color-action h-4" /> : null}
              </div>
            </FormRadio>
            <ul className="mt-4">
              <li className="list-disc text-sm ml-6">A CNAME record is configured in DNS for each domain</li>
              <li className="list-disc text-sm ml-6">Best choice when Path Based Routing is required</li>
              <li className="list-disc text-sm ml-6">Maintain full control over DNS</li>
              <li className="list-disc text-sm ml-6">CDN/WAF Compatible</li>
            </ul>
          </div>
          <div
            className={`mb-2 p-4 border rounded cursor-pointer radio-card ${
              dnsMode === "ns" ? "radio-card-active" : ""
            }`}
            onClick={() => setValue("advanced.spec.dnsMode", "ns")}
          >
            <FormRadio
              name="advanced.spec.dnsMode"
              control={control}
              compareValue="ns"
              rules={{ required: "DNS mode is required" }}
            >
              <div className="flex items-center gap-2">
                <div>NS</div>
                {dnsMode === "ns" ? <CheckCircleFilled className="color-action h-4" /> : null}
              </div>
            </FormRadio>
            <ul className="mt-4">
              <li className="list-disc text-sm ml-6">NS Records are used to delegate DNS to Control Plane</li>
              <li className="list-disc text-sm ml-6">
                Best choice when a unique DNS subdomain is required for each workload (GVC Subdomain Routing)
              </li>
              <li className="list-disc text-sm ml-6">One-time Configuration</li>
              <li className="list-disc text-sm ml-6">Works for all current and future workloads in a GVC</li>
            </ul>
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center">
          <NGLabelText className="cursor-default">Cert Challenge Type</NGLabelText>
          <span className="color-danger">*</span>
        </div>
        <FormNGRadioGroup
          name="advanced.spec.certChallengeType"
          control={control}
          rules={{ required: "Cert Challenge Type is required" }}
          options={[
            {
              label: "http01",
              value: "http01",
              isDisabled: routingMode === "workloadSubdomain" || dnsMode === "ns",
            },
            { label: "dns01", value: "dns01" },
          ]}
        />
      </div>
      {isDomainApex ? (
        <NGAlert className="mt-2" size="small" type={"info"} message="Apex Domain can only use DNS Mode CNAME" />
      ) : dnsMode === "ns" ? (
        <NGAlert
          className="mt-2"
          size="small"
          type={"info"}
          message="DNS Mode NS can only use Cert Challenge Type DNS01"
        />
      ) : routingMode === "workloadSubdomain" ? (
        <NGAlert
          className="mt-2"
          size="small"
          type={"info"}
          message="Workload Subdomain Routing must use Cert Challenge Type DNS01"
        />
      ) : null}
      <FormDryRunAlert path={"spec.certChallengeType"} paths={["spec.dnsMode"]} />
      <div className="mb-8" style={{ width: 900 }}>
        <div className="flex items-center">
          <NGLabelText className="cursor-default">Routing Mode</NGLabelText>
          <span className="color-danger">*</span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div
            className={`mb-2 p-4 border rounded cursor-pointer radio-card ${
              routingMode === "pathBased" ? "radio-card-active" : ""
            }`}
            onClick={() => setValue("advanced.spec.routingMode", "pathBased")}
          >
            <FormRadio
              name="advanced.spec.routingMode"
              control={control}
              compareValue="pathBased"
              rules={{ required: "Routing mode is required" }}
            >
              <div className="flex items-center gap-2">
                <div>Path Based Routing</div>
                {routingMode === "pathBased" ? <CheckCircleFilled className="color-action h-4" /> : null}
              </div>
            </FormRadio>
            <div className="text-sm mb-2 mt-4 ml-2">
              One or more paths, each mapped to a specific port on a specific workload in the GVC.
            </div>
            <div className="text-sm mb-2 mt-4 ml-2">Example Endpoints</div>
            <ul>
              <li className="text-sm ml-4">https://{name}/</li>
              <li className="text-sm ml-4">https://{name}/workload_one</li>
              <li className="text-sm ml-4">https://{name}/workload_two</li>
              <li className="text-sm ml-4">
                https://{name}/{`<any>`}
              </li>
            </ul>
          </div>
          <div
            className={`mb-2 p-4 border rounded cursor-pointer radio-card ${
              routingMode === "gvcSubdomain" ? "radio-card-active" : ""
            }`}
            onClick={() => setValue("advanced.spec.routingMode", "gvcSubdomain")}
          >
            <FormRadio name="advanced.spec.routingMode" control={control} compareValue="gvcSubdomain">
              <div className="flex items-center gap-2">
                <div>GVC Subdomain Routing</div>
                {routingMode === "gvcSubdomain" ? <CheckCircleFilled className="color-action h-4" /> : null}
              </div>
            </FormRadio>
            <div className="text-sm mb-2 mt-4 ml-2">
              A subdomain for each workload in the GVC, each automatically setup to route traffic to the first port
              found for the workload.
            </div>
            <div className="text-sm mb-2 mt-4 ml-2">Example Endpoints</div>
            <ul>
              <li className="text-sm ml-4">https://workload_one.{name}</li>
              <li className="text-sm ml-4">https://workload_two.{name}</li>
            </ul>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div
            className={`mb-2 p-4 border rounded cursor-pointer radio-card ${
              routingMode === "workloadSubdomain" ? "radio-card-active" : ""
            }`}
            onClick={() => setValue("advanced.spec.routingMode", "workloadSubdomain")}
          >
            <FormRadio name="advanced.spec.routingMode" control={control} compareValue="workloadSubdomain">
              <div className="flex items-center gap-2">
                <div>Workload Subdomain Replica Routing</div>
                {routingMode === "workloadSubdomain" ? <CheckCircleFilled className="color-action h-4" /> : null}
              </div>
            </FormRadio>
            <div className="text-sm mb-2 mt-4 ml-2">A subdomain for each replica of a stateful workload.</div>
            <div className="text-sm mb-2 mt-4 ml-2">Naming Format</div>
            <ul>
              <li className="font-mono text-sm ml-4">$workloadName-$replicaNumber-$location.$domainName</li>
            </ul>
            <div className="text-sm mb-2 mt-4 ml-2">Example Endpoints</div>
            <ul>
              <li className="text-sm ml-4">https://workload_one-1-aws-eu-central-1.{name}</li>
              <li className="text-sm ml-4">https://workload_one-2-aws-eu-central-1.{name}</li>
            </ul>
          </div>
          <div
            className={`mb-2 p-4 border rounded cursor-pointer radio-card ${
              routingMode === "none" ? "radio-card-active" : ""
            }`}
            onClick={() => setValue("advanced.spec.routingMode", "none")}
          >
            <FormRadio
              name="advanced.spec.routingMode"
              control={control}
              compareValue="none"
              rules={{ required: "Routing mode is required" }}
            >
              <div className="flex items-center gap-2">
                <div>None</div>
                {routingMode === "none" ? <CheckCircleFilled className="color-action h-4" /> : null}
              </div>
            </FormRadio>
            <div className="mt-4 text-sm ml-2">No routes in ports.</div>
          </div>
        </div>
      </div>
      {routingMode === "none" ? null : (
        <>
          <NGLabelText className="cursor-default">Wildcard</NGLabelText>
          <div>Accept all hosts is a full wildcard and will accept any arbitrary host.</div>
          <div>Accept all subdomains will accept any host that is a sub domain of the domain so *.$DOMAIN</div>
          <div className="mb-8 mt-2">
            <FormNGCheckboxGroup
              name="advanced.spec.acceptAll"
              control={control}
              options={[
                {
                  label: <div style={{ color: "var(--color-default)" }}>Accept All Hosts</div>,
                  value: "hosts",
                },
                {
                  label: <div style={{ color: "var(--color-default)" }}>Accept All Subdomains</div>,
                  value: "subdomains",
                },
              ]}
            />
          </div>
          <FormDryRunAlert path={"spec.acceptAllHosts"} paths={["spec.acceptAllSubdomains"]} />
        </>
      )}
    </div>
  );
};
